import React, { useState } from 'react';
import { 
  ChevronDown,
  ChevronUp,
  Edit,
  Trash2,
  User,
  Calendar,
  Flag,
  MessageSquare,
  Paperclip,
  MoreHorizontal
} from 'lucide-react';
import TaskDetailPanelPro from './TaskDetailPanelPro';
import { taskService } from '../../services/taskService';
import toast from 'react-hot-toast';

/**
 * TaskList - List view component for displaying tasks
 */
const TaskList = ({ 
  tasks = [], 
  loading = false, 
  pagination = {}, 
  onTaskUpdate, 
  onTaskDelete, 
  onLoadMore 
}) => {
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [deletingTaskId, setDeletingTaskId] = useState(null);

  /**
   * Get priority color
   */
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  /**
   * Get status color
   */
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'blocked':
        return 'bg-red-100 text-red-800';
      case 'under_review':
        return 'bg-purple-100 text-purple-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  /**
   * Check if task is overdue
   */
  const isOverdue = (task) => {
    if (!task.due_date || task.status === 'completed' || task.status === 'cancelled') {
      return false;
    }
    return new Date(task.due_date) < new Date();
  };

  /**
   * Toggle task expansion
   */
  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  /**
   * Handle opening task details
   */
  const handleOpenTask = (task) => {
    setSelectedTask(task);
    setShowDetailPanel(true);
  };

  /**
   * Handle delete task
   */
  const handleDeleteTask = async (task) => {
    if (!confirm(`Are you sure you want to delete "${task.title}"?`)) {
      return;
    }

    try {
      setDeletingTaskId(task.id);
      await taskService.deleteTask(task.id);
      onTaskDelete && onTaskDelete(task.id);
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error(`Failed to delete task: ${error.message}`);
    } finally {
      setDeletingTaskId(null);
    }
  };

  /**
   * Handle task updated
   */
  const handleTaskUpdated = (updatedTask) => {
    onTaskUpdate && onTaskUpdate(updatedTask);
    // Keep panel open so user can see the updated task
    setSelectedTask(updatedTask);
  };

  /**
   * Handle quick status update
   */
  const handleQuickStatusUpdate = async (task, newStatus) => {
    try {
      const updatedTask = await taskService.updateTaskStatus(task.id, newStatus);
      onTaskUpdate && onTaskUpdate(updatedTask.task);
      toast.success('Task status updated');
    } catch (error) {
      console.error('Failed to update task status:', error);
      toast.error('Failed to update task status');
    }
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
              <div className="flex space-x-2">
                <div className="h-6 bg-gray-200 rounded w-16"></div>
                <div className="h-6 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (tasks.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <MessageSquare className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
        <p className="text-gray-500">
          {loading ? 'Loading tasks...' : 'Create your first task to get started.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {tasks.map((task) => {
        const isExpanded = expandedTasks.has(task.id);
        const overdue = isOverdue(task);
        
        return (
          <div
            key={task.id}
            className={`bg-white rounded-lg border ${
              overdue ? 'border-red-200 bg-red-50' : 'border-gray-200'
            } overflow-hidden`}
          >
            {/* Main Task Row */}
            <div className="p-4">
              <div 
                className="flex items-center justify-between cursor-pointer hover:bg-gray-50 rounded-lg -m-2 p-2 transition-colors duration-150"
                onClick={() => handleOpenTask(task)}
                title="Click to open task details"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTaskExpansion(task.id);
                      }}
                      className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1 hover:bg-gray-200 rounded transition-colors"
                      title="Toggle task details"
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 truncate">
                        {task.title}
                      </h3>
                      <div className="flex items-center space-x-4 mt-1 text-xs text-gray-500">
                        <span>{task.control_id}</span>
                        {task.assignee && (
                          <span className="flex items-center">
                            <User className="h-3 w-3 mr-1" />
                            {task.assignee.email}
                          </span>
                        )}
                        {task.due_date && (
                          <span className={`flex items-center ${overdue ? 'text-red-600' : ''}`}>
                            <Calendar className="h-3 w-3 mr-1" />
                            {formatDate(task.due_date)}
                            {overdue && ' (Overdue)'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 ml-4">
                  {/* Priority Badge */}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                    <Flag className="h-3 w-3 mr-1" />
                    {task.priority}
                  </span>
                  
                  {/* Status Badge */}
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                    {task.status.replace('_', ' ')}
                  </span>
                  
                  {/* Comments/Attachments indicators */}
                  <div className="flex items-center space-x-2 text-gray-400">
                    {task.task_comments && task.task_comments.length > 0 && (
                      <span className="flex items-center text-xs">
                        <MessageSquare className="h-3 w-3 mr-1" />
                        {task.task_comments.length}
                      </span>
                    )}
                    {task.task_attachments && task.task_attachments.length > 0 && (
                      <span className="flex items-center text-xs">
                        <Paperclip className="h-3 w-3 mr-1" />
                        {task.task_attachments.length}
                      </span>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenTask(task);
                      }}
                      className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Open task details"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTask(task);
                      }}
                      disabled={deletingTaskId === task.id}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50 transition-colors"
                      title="Delete task"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Expanded Content */}
            {isExpanded && (
              <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
                <div className="space-y-3">
                  {/* Description */}
                  {task.description && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-1">Description</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{task.description}</p>
                    </div>
                  )}
                  
                  {/* Quick Actions */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Quick Actions</h4>
                    <div className="flex flex-wrap gap-2">
                      {task.status !== 'completed' && (
                        <button
                          onClick={() => handleQuickStatusUpdate(task, 'completed')}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200"
                        >
                          Mark Complete
                        </button>
                      )}
                      
                      {task.status === 'not_started' && (
                        <button
                          onClick={() => handleQuickStatusUpdate(task, 'in_progress')}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                        >
                          Start Task
                        </button>
                      )}
                      
                      {task.status === 'in_progress' && (
                        <button
                          onClick={() => handleQuickStatusUpdate(task, 'under_review')}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-purple-700 bg-purple-100 hover:bg-purple-200"
                        >
                          Submit for Review
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Metadata */}
                  <div className="text-xs text-gray-500 flex items-center space-x-4">
                    <span>Created: {formatDate(task.created_at)}</span>
                    <span>Updated: {formatDate(task.updated_at)}</span>
                    {task.creator && (
                      <span>By: {task.creator.email}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      
      {/* Load More Button */}
      {pagination.hasMore && (
        <div className="text-center py-4">
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
      
      {/* Task Detail Panel */}
      <TaskDetailPanelPro
        task={selectedTask}
        isOpen={showDetailPanel}
        onClose={() => {
          setShowDetailPanel(false);
          setSelectedTask(null);
        }}
        onTaskUpdated={handleTaskUpdated}
      />
    </div>
  );
};

export default TaskList;